/*
===============================================================================================================
                                 Copyright 2022-2025, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import company from './company';
import assetManagement from './asset-management';
import warehouse from './warehouse';
import warehouseBindings from './warehouse-bindings';
import onboard from './onboard';
import signup from './signup';
import companyCarrier from './company-carrier';
import companyNetwork from './company-network';
import rules from './rules';

/**
 * Publishes configuration changes to various BE services based on change types
 *
 * @param {Object} publishParams - The publishing parameters
 * @param {Array} publishParams.changes - Array of change objects with type property
 * @param {string} publishParams.merchantId - Merchant companyId
 * @param {string} publishParams.token - Authentication token
 * @param {string} publishParams.rootOrgId - Root organization ID
 * @param {string} publishParams.productName - Name of the product within the company record
 * @param {Object} publishParams.warehouseAddress - A default warehouse address object
 * @param {string} publishParams.applicationVersion - Version of the application-config.json from asset-management
 * @param {Function} publishParams.getConfigValue - Function to retrieve merchant config values from MerchantContext
 * @param {Function} publishParams.getFeature - Function to retrieve merchant feature settings from MerchantContext
 * @param {Function} publishParams.getAdminConfigValue - Function to retrieve admin config values from AdminContext
 * @param {Function} publishParams.getAdminFeature - Function to retrieve admin feature settings from AdminContext
 * @param {Object} publishParams.sharedData - Shared data object containing common information
 * @param {Function} publishParams.getLocaleContextValue - Function to retrieve values from LocaleContext
 * @param {boolean} publishParams.newAddressEnabledByDefault - Flag indicating if new address is enabled by default
 * @param {string} publishParams.homeCountryCode - Home country code value from MerchantContext
 * @param {boolean} publishParams.homeCountryCodeSetToWarehouse - A feature setting from MerchantContext
 * @returns {Promise<void>} Promise that resolves when all publishing operations complete
 */
const publish = async (publishParams) => {
  // generates a changes array specific to each publishing function
  const getChanges = (type) => publishParams.changes.filter((change) => change.type === type);

  await company({ ...publishParams, changes: getChanges('COMPANY') });
  await companyCarrier({ ...publishParams, changes: getChanges('COMPANY_CARRIER_CONFIG') });
  await companyNetwork({ ...publishParams, changes: getChanges('COMPANY_NETWORK_CONFIG') });
  await warehouse({ ...publishParams, changes: getChanges('WAREHOUSE').concat(getChanges('WAREHOUSE_BINDINGS')) });
  await warehouseBindings({ ...publishParams, changes: getChanges('WAREHOUSE_BINDINGS') });
  await onboard({ ...publishParams, changes: getChanges('ONBOARD') });
  await signup({ ...publishParams, changes: getChanges('SIGNUP') });
  await assetManagement({ ...publishParams, changes: getChanges('APPLICATION_CONFIG_MERCHANT') });
  await assetManagement({ ...publishParams, merchantId: publishParams.rootOrgId, changes: getChanges('APPLICATION_CONFIG_OWNER') });
  await rules({ ...publishParams, changes: getChanges('RULES') });
};

export default publish;
